/*
 * Brinde automático — uma linha real, gratuita, por carrinho da Loja virtual.
 * O produto vem de Configurações do tema > Brinde automático (layout/theme.liquid
 * só carrega este arquivo quando o produto é válido e tem preço zero).
 *
 * Regra: se houver outro produto no carrinho, o brinde fica com quantidade 1;
 * sem outro produto, o brinde sai.
 */
(() => {
  const config = window.lvGiftConfig;
  if (!config || !Number(config.variantId) || !Number(config.productId)) return;

  const variantId = Number(config.variantId);
  const productId = Number(config.productId);
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cartUrl = `${root.endsWith('/') ? root : `${root}/`}cart`;
  const isCartPath = path => path.replace(/\/$/, '') === cartUrl;
  const sectionOptions = isCartPath(location.pathname) ? {} : { sections: ['mini-cart'] };
  let inFlight = null;
  let pending = false;
  let checkingOut = false;
  const staleRefreshes = new WeakSet();
  let pendingThemeRefresh = null;
  let pendingOpenMiniCart = false;
  let cartKnownCorrect = Boolean(config.initialCartCorrect);

  async function request(path, body) {
    const response = await fetch(`${cartUrl}${path}`, {
      credentials: 'same-origin',
      ...(body ? {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      } : {})
    });
    if (!response.ok) {
      const error = new Error(`Gift cart request failed: ${response.status}`);
      error.status = response.status;
      throw error;
    }
    return response.json();
  }

  function isGift(item) {
    return Number(item.product_id) === productId;
  }

  function qualifies(item) {
    return !isGift(item);
  }

  function isCorrect(cart) {
    const regularItems = cart.items.filter(qualifies);
    const giftItems = cart.items.filter(isGift);
    return regularItems.length
      ? giftItems.length === 1 && Number(giftItems[0].variant_id) === variantId && giftItems[0].quantity === 1
      : giftItems.length === 0;
  }

  function closeDrawerIfOnlyGiftRemains(cart) {
    if (!cart.items.every(isGift)) return;
    const drawer = document.getElementById('mini-cart');
    if (drawer && drawer.open) drawer.open = false;
  }

  async function reconcile(initialCart) {
    let cart = initialCart || await request('.js');
    let changed = false;
    let sections = null;

    // change.js devolve o carrinho completo. add.js devolve a linha adicionada;
    // aproveite-a com a seção renderizada para mostrar o brinde sem outro GET.
    for (let attempt = 0; attempt < 12 && !isCorrect(cart); attempt += 1) {
      const regularItems = cart.items.filter(qualifies);
      const giftItems = cart.items.filter(isGift);
      const chosen = giftItems.find(item => Number(item.variant_id) === variantId);
      let response;

      if (!regularItems.length || giftItems.some(item => Number(item.variant_id) !== variantId) || giftItems.length > 1) {
        const remove = !regularItems.length ? giftItems[0] : giftItems.find(item => item !== chosen) || giftItems[1];
        response = await request('/change.js', { id: remove.key, quantity: 0, ...sectionOptions });
        cart = response;
      } else if (!chosen) {
        response = await request('/add.js', { items: [{ id: variantId, quantity: 1 }], ...sectionOptions });
        const addedItems = Array.isArray(response.items) ? response.items : [response];
        if (addedItems.length !== 1 || !isGift(addedItems[0])) {
          cart = await request('.js');
        } else {
          cart = {
            ...cart,
            items: [...cart.items, addedItems[0]],
            item_count: Number(cart.item_count) + Number(addedItems[0].quantity)
          };
        }
      } else {
        response = await request('/change.js', { id: chosen.key, quantity: 1, ...sectionOptions });
        cart = response;
      }

      changed = true;
      sections = response.sections && typeof response.sections['mini-cart'] === 'string' ? response.sections : null;
    }

    if (!isCorrect(cart)) throw new Error('Gift quantity could not be corrected');
    if (sections) cart.sections = sections;
    cartKnownCorrect = true;
    return { cart, changed };
  }

  function sync(initialCart) {
    if (inFlight) {
      pending = true;
      return inFlight;
    }
    inFlight = (async () => {
      let result;
      let changed = false;
      do {
        pending = false;
        result = await reconcile(initialCart);
        changed = changed || result.changed;
        initialCart = null;
      } while (pending);

      const cart = result.cart;
      if ((changed || pendingThemeRefresh) && !checkingOut) {
        if (isCartPath(location.pathname)) {
          location.reload();
          return cart;
        }
        closeDrawerIfOnlyGiftRemains(cart);
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true, detail: { cart, source: 'lv-free-gift' }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart, source: 'lv-free-gift', replacementDelay: 1, openMiniCart: pendingOpenMiniCart }
        }));
      }
      pendingThemeRefresh = null;
      pendingOpenMiniCart = false;
      return cart;
    })().catch(error => {
      cartKnownCorrect = false;
      // Se a sincronização falhar, mostre ao menos a atualização original do tema.
      if (pendingThemeRefresh) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true, detail: { ...pendingThemeRefresh, source: 'lv-free-gift-fallback' }
        }));
      }
      pendingThemeRefresh = null;
      pendingOpenMiniCart = false;
      throw error;
    }).finally(() => { inFlight = null; });
    return inFlight;
  }

  function submitToCheckout(form) {
    if (!form.querySelector('input[name="checkout"]')) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'checkout';
      form.appendChild(input);
    }
    HTMLFormElement.prototype.submit.call(form);
  }

  document.addEventListener('cart:updated', event => {
    if (event.detail && event.detail.source === 'lv-free-gift') return;
    const cart = event.detail && event.detail.cart;
    if (cart && Array.isArray(cart.items)) {
      closeDrawerIfOnlyGiftRemains(cart);
      cartKnownCorrect = isCorrect(cart);
      if (cartKnownCorrect) return;
      staleRefreshes.add(cart);
      sync(cart).catch(console.error);
    } else {
      sync().catch(console.error);
    }
  });

  // O Focal envia um refresh imediatamente após cart:updated. Quando falta
  // corrigir o brinde, bloqueie só esse HTML antigo e envie o HTML correto.
  document.addEventListener('cart:refresh', event => {
    const detail = event.detail;
    if (!detail || detail.source === 'lv-free-gift' || detail.source === 'lv-free-gift-fallback') return;
    if (detail.cart && staleRefreshes.has(detail.cart)) {
      staleRefreshes.delete(detail.cart);
      pendingThemeRefresh = detail;
      pendingOpenMiniCart = pendingOpenMiniCart || Boolean(detail.openMiniCart);
      event.stopImmediatePropagation();
    }
  }, true);

  // A vitrine não consulta o carrinho ao carregar. Ao abrir a gaveta, confirme
  // que um carrinho de uma visita anterior continua com o brinde correto.
  document.addEventListener('click', event => {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    const trigger = target.closest('[aria-controls="mini-cart"]');
    if (trigger && trigger.getAttribute('aria-expanded') !== 'true' && !cartKnownCorrect) {
      sync().catch(console.error);
    }
  }, true);

  // O botão de checkout pode enviar o formulário antes de o brinde entrar no carrinho. Espera a sincronização.
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !isCartPath(new URL(form.action, location.href).pathname)) return;
    if (!form.querySelector('[name="checkout"]') && (!event.submitter || event.submitter.name !== 'checkout')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (form.dataset.lvGiftSubmitting) return;
    form.dataset.lvGiftSubmitting = 'true';
    checkingOut = true;

    sync().then(cart => {
      if (!isCorrect(cart)) throw new Error('Gift is missing from the cart');
      submitToCheckout(form);
    }).catch(error => {
      console.error(error);
      // 422 = brinde esgotado ou indisponível: segue para o checkout sem ele, para não travar a venda.
      if (error.status === 422) {
        submitToCheckout(form);
        return;
      }
      checkingOut = false;
      delete form.dataset.lvGiftSubmitting;
      form.querySelectorAll('[aria-busy]').forEach(button => button.removeAttribute('aria-busy'));
      document.querySelectorAll(`button[form="${form.id}"][aria-busy]`).forEach(button => button.removeAttribute('aria-busy'));
      alert('We could not add your free gift. Please try again.');
    });
  }, true);

  // Na página do carrinho, a reconciliação inicial protege o checkout direto.
  if (isCartPath(location.pathname) && !cartKnownCorrect) {
    sync().catch(console.error);
  }
})();
